import cx from 'classnames';
import { FormControlLabel } from './FormControlLabel';
import { FormControlContext } from './useFormControl';
import { FormControlHelpText } from './FormControlHelpText';
import { getChildren } from './utils';
import { useFormContext } from './Form';
import { FormLabelSize, FormSize } from './types';
import { DetailedHTMLProps, FieldsetHTMLAttributes, Children, ReactNode, isValidElement, MouseEvent } from 'react';

export interface IFormControlProps
  extends DetailedHTMLProps<FieldsetHTMLAttributes<HTMLFieldSetElement>, HTMLFieldSetElement> {
  children?: ReactNode;
  required?: boolean;
  hasError?: boolean;
  readOnly?: boolean;
  size?: FormSize;
  labelSize?: FormLabelSize;
}

export const FormControl = (props: IFormControlProps) => {
  const {
    children,
    required,
    hasError,
    readOnly,
    className,
    size: propSize,
    labelSize: propLabelSize,
    ...restProps
  } = props;
  const { size: contextSize, labelSize: contextLabelSize, ...form } = useFormContext() || {};

  const size = propSize || contextSize || 'medium';
  const labelSize = propLabelSize || contextLabelSize || 'large';

  const { requestedElements } = getChildren(children, [FormControlLabel, FormControlHelpText]);

  const handleLabelClick = (e: MouseEvent<HTMLLabelElement>) => {
    const parent = e.currentTarget.parentElement;
    const input: any = parent?.querySelector('input, select, textarea');
    if (input) {
      input.focus();
    }
  };

  const childrenArray = Children.toArray(children);

  const orderedElements = childrenArray
    .map((child) => {
      if (isValidElement(child)) {
        if (child.type === FormControlLabel) {
          return child;
        }

        if (child.type === FormControlHelpText) {
          return child;
        }

        if (!requestedElements[child.type as any]) {
          return (
            <div className="slds-form-element__control" key={child.key}>
              {child}
            </div>
          );
        }
      }
      return null;
    })
    .filter(Boolean);

  return (
    <FormControlContext.Provider
      value={{
        required,
        hasError,
        readOnly: readOnly ?? form.readOnly,
        onLabelClick: handleLabelClick,
        size,
        labelSize
      }}
    >
      <fieldset
        className={cx(
          'slds-form-element',
          {
            'slds-has-error': hasError
          },
          className
        )}
        {...restProps}
      >
        {orderedElements}
      </fieldset>
    </FormControlContext.Provider>
  );
};
